import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBars, faBook, faCircleQuestion, faClipboardQuestion, faKey } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { catchError, switchMap } from 'rxjs';
import { AboutComponent } from 'src/app/controls/about/about.component';
import { EnumModeResetPassword } from 'src/app/models/enums/enum-mode-reset-password';
import { EnumReasonResetPassword } from 'src/app/models/enums/enum-reason-reset-password';
import { ExternalParameters } from 'src/app/models/settings/external-parameters';
import { MemoryService } from 'src/app/services/memory.service';
import { SettingsService } from 'src/app/services/settings.service';
import { UsersService } from 'src/app/services/users.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-banner-menu',
    templateUrl: './banner-menu.component.html',
    styleUrls: ['./banner-menu.component.scss'],
    imports: [MatTooltipModule, MatMenuModule, FaIconComponent],
})
export class BannerMenuComponent {
    private toastrService = inject(ToastrService);
    private spinner = inject(NgxSpinnerService);
    private memoryService = inject(MemoryService);
    private settingsService = inject(SettingsService);
    private destroyRef = inject(DestroyRef);
    private usersService = inject(UsersService);

    public menuIcon = faBars;
    public handbookIcon = faBook;
    public faqIcon = faClipboardQuestion;
    public aboutIcon = faCircleQuestion;
    public faKey = faKey;

    constructor(public dialog: MatDialog) {}

    /**
     * Affichage d'une popup pour les informations générale du produit.
     */
    public openAbout(): void {
        this.dialog.open(AboutComponent, {
            maxWidth: '400px',
            panelClass: 'empty-dialog',
        });
    }

    modifyPassword() {
        if (this.memoryService.getConnectedUser().organization.officeDomain) {
            this.toastrService.warning('Veuillez-vous rapprocher de votre service informatique pour la modification de votre mot de passe Microsoft.');
        } else {
            this.spinner.show('main-spinner');

            this.settingsService
                .getSettings()
                .pipe(
                    switchMap((x) => {
                        let gumUrl: string;
                        try {
                            gumUrl = x.apps['gum']['url'];
                        } catch {
                            gumUrl = null;
                        }

                        if (!environment.production && gumUrl === null) {
                            gumUrl = 'http://10.20.0.10:9030';
                        }

                        if (gumUrl) {
                            let parameters = new ExternalParameters();
                            parameters.name = 'Hub4B - Back Office';
                            parameters.return = encodeURIComponent(`${window.location.origin}/home`);
                            let redirectUrl = encodeURIComponent(
                                `${gumUrl}/changepassword?resetPassword=${this.memoryService.getConnectedUser().userId}&parameters=${JSON.stringify(parameters)}`
                            );

                            return this.usersService.StartPasswordReset(
                                this.memoryService.getConnectedUser().userId,
                                EnumReasonResetPassword.Modify,
                                EnumModeResetPassword.Mail,
                                redirectUrl
                            );
                        } else {
                            throw 'Domain Error';
                        }
                    }),
                    catchError((err) => {
                        throw err;
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: () => {
                        this.spinner.hide('main-spinner');

                        const emailContact = this.memoryService.getConnectedUser().login;

                        this.toastrService.success(
                            `Un message a été envoyé à \"${emailContact}\" pour valider la réinitialisation du mot de passe.<br /><br />En cas de non réception vérifier votre adresse mail ou les courriers indésirables.`,
                            '',
                            {
                                enableHtml: true,
                            }
                        );
                    },
                    error: (err: unknown) => {
                        this.spinner.hide('main-spinner');

                        if (err === 'Domain Error') {
                            this.toastrService.warning("Votre domaine n'est pas correctement configuré.<br />Veuillez contacter votre administrateur.", '', { enableHtml: true });
                        } else {
                            const error = err as HttpErrorResponse;

                            if (error.status === 412) {
                                this.toastrService.error(
                                    "L'envoi de mail pour le changement de mot de passe ne peut pas être effectué. Veuillez contacter votre administrateur.",
                                    '',
                                    { enableHtml: true }
                                );
                            } else {
                                this.toastrService.error("Erreur lors de l'envoi de mail pour le changement de mot de passe. Veuillez contacter votre administrateur.", '', {
                                    enableHtml: true,
                                });
                            }
                        }
                    },
                });
        }
    }
}
